import type { LayoutDocument, TargetKey } from "./types.js";
import { DEFAULT_USER_LAYOUT_DISCIPLINE_KEY } from "./types.js";

/**
 * Runtime asset kinds supported by the public layout relay.
 */
export type RuntimeLayoutAssetKind = "image" | "video" | "font";

/**
 * Font file formats supported by the runtime relay.
 */
export type RuntimeLayoutFontFormat = "woff2" | "ttf" | "otf";

/**
 * One asset referenced by a published runtime layout snapshot.
 */
export interface RuntimeLayoutAssetReference {
  /** Asset category used to resolve the public runtime endpoint. */
  kind: RuntimeLayoutAssetKind;
  /** Basename stored on disk under `user-data/<userId>/<kind>s`. */
  fileName: string;
  /** Font family name exposed to the renderer for custom fonts. */
  family?: string;
  /** Font format used to build the `FontFace` descriptor. */
  format?: RuntimeLayoutFontFormat;
}

/**
 * Concrete layout document published for a single runtime target.
 */
export interface RuntimeLayoutEntry {
  /** Shared target key (`current_athlete`, `athlete_display`, ...) or custom target key. */
  targetKey: TargetKey | string;
  /** Human-readable target label used by runtime catalogs. */
  targetLabel?: string;
  /** Discipline scope for this runtime target. */
  disciplineKey?: string;
  /** Local custom target id when this entry comes from a customLayoutTargets row. */
  customTargetId?: string;
  /** Expected runtime page type for the target. */
  type: "widget" | "display";
  /** Fully resolved layout document for the target. */
  document: LayoutDocument;
}

/**
 * Snapshot emitted by Electron and stored by the websocket server.
 */
export interface RuntimeLayoutPublishPayload {
  /** Meet identifier scoping the whole snapshot. */
  meetId: string;
  /** Current live theme name used to resolve the layouts. */
  themeName: string | null;
  /** User owning the uploaded assets referenced by the selected theme. */
  assetOwnerUserId: string | null;
  /** All resolved layouts currently available for this meet. */
  layouts: RuntimeLayoutEntry[];
  /** Flat inventory of assets referenced anywhere in the published layouts. */
  assets: RuntimeLayoutAssetReference[];
  /** Timestamp used for stale-state detection in clients. */
  publishedAt: string;
}

/**
 * Public font descriptor exposed to runtime clients.
 */
export interface RuntimeLayoutFontFace {
  fileName: string;
  family: string;
  format: RuntimeLayoutFontFormat;
  fontUrl: string;
}

/**
 * Snapshot sent by the websocket server to public runtime clients.
 */
export interface RuntimeLayoutSnapshot {
  /** Meet identifier associated with the snapshot. */
  meetId: string;
  /** Current live theme name, when available. */
  themeName: string | null;
  /** Layout documents rewritten to public runtime asset URLs. */
  layouts: RuntimeLayoutEntry[];
  /** Custom fonts available for the published layouts. */
  fonts: RuntimeLayoutFontFace[];
  /** Timestamp copied from the original publish payload. */
  publishedAt: string;
}

/**
 * Minimal request payload used by a runtime viewer to subscribe to one meet.
 */
export interface RuntimeLayoutSnapshotRequest {
  meetId: string;
}

/**
 * Complete asset usage extracted from a layout document.
 */
export interface RuntimeLayoutAssetUsage {
  imageFileNames: string[];
  videoFileNames: string[];
  fontFileNames: string[];
  fontFamilies: string[];
}

const IMAGE_MARKER = "/user-layouts/images/";
const VIDEO_MARKER = "/user-layouts/videos/";
const FONT_MARKER = "/user-layouts/fonts/";

/**
 * Shared websocket event names for the public runtime layout relay.
 */
export const RUNTIME_LAYOUT_EVENT = {
  PUBLISH_SNAPSHOT: "runtime-layout:publish-snapshot",
  REQUEST_SNAPSHOT: "runtime-layout:request-snapshot",
  SNAPSHOT: "runtime-layout:snapshot",
} as const;

/**
 * Rewrites every protected `/user-layouts/*` URL found anywhere in a layout
 * document, including primitive media nodes and CSS-style string values.
 *
 * @param document - Original layout document.
 * @param rewriteUrl - Callback returning the public replacement URL.
 * @returns A cloned layout document safe for the target runtime.
 */
export function rewriteProtectedLayoutAssetUrls(
  document: LayoutDocument,
  rewriteUrl: (asset: {
    kind: RuntimeLayoutAssetKind;
    fileName: string;
    rawUrl: string;
  }) => string,
): LayoutDocument {
  const json = JSON.stringify(document);
  const rewritten = rewriteProtectedAssetUrlsInText(json, rewriteUrl);
  return JSON.parse(rewritten) as LayoutDocument;
}

/**
 * Extracts the protected asset references and custom font family names used by
 * a layout document.
 *
 * @param document - Layout document to inspect.
 * @returns Flat usage lists deduplicated by file name / family.
 */
export function collectRuntimeLayoutAssetUsage(
  document: LayoutDocument,
): RuntimeLayoutAssetUsage {
  const json = JSON.stringify(document);
  const imageFileNames = new Set<string>();
  const videoFileNames = new Set<string>();
  const fontFileNames = new Set<string>();
  const fontFamilies = new Set<string>();

  collectProtectedAssetFileNames(json, IMAGE_MARKER, imageFileNames);
  collectProtectedAssetFileNames(json, VIDEO_MARKER, videoFileNames);
  collectProtectedAssetFileNames(json, FONT_MARKER, fontFileNames);
  collectFontFamilies(document, fontFamilies);

  return {
    imageFileNames: [...imageFileNames].sort(),
    videoFileNames: [...videoFileNames].sort(),
    fontFileNames: [...fontFileNames].sort(),
    fontFamilies: [...fontFamilies].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" }),
    ),
  };
}

function rewriteProtectedAssetUrlsInText(
  text: string,
  rewriteUrl: (asset: {
    kind: RuntimeLayoutAssetKind;
    fileName: string;
    rawUrl: string;
  }) => string,
): string {
  let result = text;
  result = replaceAssetMarker(result, IMAGE_MARKER, "image", rewriteUrl);
  result = replaceAssetMarker(result, VIDEO_MARKER, "video", rewriteUrl);
  result = replaceAssetMarker(result, FONT_MARKER, "font", rewriteUrl);
  return result;
}

function replaceAssetMarker(
  text: string,
  marker: string,
  kind: RuntimeLayoutAssetKind,
  rewriteUrl: (asset: {
    kind: RuntimeLayoutAssetKind;
    fileName: string;
    rawUrl: string;
  }) => string,
): string {
  const pattern = new RegExp(`${escapeRegExp(marker)}([^"'?#\\\\\\s]+)`, "g");
  return text.replace(pattern, (_fullMatch, fileName: string) => {
    const rawUrl = `${marker}${fileName}`;
    return rewriteUrl({ kind, fileName, rawUrl });
  });
}

function collectProtectedAssetFileNames(
  text: string,
  marker: string,
  output: Set<string>,
): void {
  const pattern = new RegExp(`${escapeRegExp(marker)}([^"'?#\\\\\\s]+)`, "g");
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const fileName = match[1]?.trim();
    if (fileName) {
      output.add(fileName);
    }
  }
}

function collectFontFamilies(value: unknown, output: Set<string>): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      collectFontFamilies(item, output);
    }
    return;
  }

  if (!value || typeof value !== "object") {
    return;
  }

  for (const [key, child] of Object.entries(value)) {
    if (key === "fontFamily" && typeof child === "string") {
      const trimmed = child.trim();
      if (trimmed.length > 0) {
        output.add(trimmed);
      }
    }
    collectFontFamilies(child, output);
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Stable fingerprint of a runtime layout snapshot used to skip redundant React
 * updates when the websocket relay republishes identical layout content.
 *
 * @param snapshot - Public runtime snapshot received by web clients.
 * @returns A comparable string that ignores `publishedAt` timestamps.
 */
export function getRuntimeLayoutSnapshotContentFingerprint(
  snapshot: RuntimeLayoutSnapshot,
): string {
  const layoutPart = snapshot.layouts
    .map(
      (entry) =>
        `${entry.targetKey}:${entry.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY}:${entry.type}:${entry.document.meta.id}:${entry.document.meta.updatedAt}`,
    )
    .sort()
    .join("|");
  const fontPart = snapshot.fonts
    .map((font) => `${font.fileName}:${font.family}:${font.format}`)
    .sort()
    .join("|");

  return [snapshot.meetId, snapshot.themeName ?? "", layoutPart, fontPart].join(
    "\0",
  );
}

/**
 * Stable fingerprint of a publish payload before public URL rewriting.
 *
 * @param payload - Snapshot published by Electron.
 * @returns A comparable string that ignores `publishedAt` timestamps.
 */
export function getRuntimeLayoutPublishContentFingerprint(
  payload: RuntimeLayoutPublishPayload,
): string {
  const layoutPart = payload.layouts
    .map(
      (entry) =>
        `${entry.targetKey}:${entry.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY}:${entry.type}:${entry.document.meta.id}:${entry.document.meta.updatedAt}`,
    )
    .sort()
    .join("|");
  const assetPart = payload.assets
    .map((asset) => `${asset.kind}:${asset.fileName}`)
    .sort()
    .join("|");

  return [
    payload.meetId,
    payload.themeName ?? "",
    payload.assetOwnerUserId ?? "",
    layoutPart,
    assetPart,
  ].join("\0");
}
